import React, { useMemo } from 'react';
import { BackgroundThemeRevision, ColorId } from 'referral-types';
import { rgbStringToHex } from '../../../utils/colorUtils';
import { useTheme } from '../../../context/theme/useTheme';
import { useBodyStyleVersion } from '../../../hooks/useBodyStyleVersion';
import { getCssVariableForColorId } from '../../../constants';

const SUNBURST_PATH =
  'M998.7 439.2c1.7-26.5 1.7-52.7 0.1-78.5L401 399.9c0 0 0-0.1 0-0.1l587.6-116.9c-5.1-25.9-11.9-51.2-20.3-75.8L400.9 399.7c0 0 0-0.1 0-0.1l537.3-265c-11.6-23.5-24.8-46.2-39.3-67.9L400.8 399.5c0 0 0-0.1-0.1-0.1l450.4-395c-17.3-19.7-35.8-38.2-55.5-55.5l-395 450.4c0 0-0.1 0-0.1-0.1L733.4-99c-21.7-14.5-44.4-27.6-68-39.3l-265 537.4c0 0-0.1 0-0.1 0l192.6-567.4c-24.6-8.3-49.9-15.1-75.8-20.2L400.2 399c0 0-0.1 0-0.1 0l39.2-597.7c-26.5-1.7-52.7-1.7-78.5-0.1L399.9 399c0 0-0.1 0-0.1 0L282.9-188.6c-25.9 5.1-51.2 11.9-75.8 20.3l192.6 567.4c0 0-0.1 0-0.1 0l-265-537.3c-23.5 11.6-46.2 24.8-67.9 39.3l332.8 498.1c0 0-0.1 0-0.1 0.1L4.4-51.1C-15.3-33.9-33.8-15.3-51.1 4.4l450.4 395c0 0 0 0.1-0.1 0.1L-99 66.6c-14.5 21.7-27.6 44.4-39.3 68l537.4 265c0 0 0 0.1 0 0.1l-567.4-192.6c-8.3 24.6-15.1 49.9-20.2 75.8L399 399.8c0 0 0 0.1 0 0.1l-597.7-39.2c-1.7 26.5-1.7 52.7-0.1 78.5L399 400.1c0 0 0 0.1 0 0.1l-587.6 116.9c5.1 25.9 11.9 51.2 20.3 75.8l567.4-192.6c0 0 0 0.1 0 0.1l-537.3 265c11.6 23.5 24.8 46.2 39.3 67.9l498.1-332.8c0 0 0 0.1 0.1 0.1l-450.4 395c17.3 19.7 35.8 38.2 55.5 55.5l395-450.4c0 0 0.1 0 0.1 0.1L66.6 899c21.7 14.5 44.4 27.6 68 39.3l265-537.4c0 0 0.1 0 0.1 0L207.1 968.3c24.6 8.3 49.9 15.1 75.8 20.2L399.8 401c0 0 0.1 0 0.1 0l-39.2 597.7c26.5 1.7 52.7 1.7 78.5 0.1L400.1 401c0 0 0.1 0 0.1 0l116.9 587.6c25.9-5.1 51.2-11.9 75.8-20.3L400.3 400.9c0 0 0.1 0 0.1 0l265 537.3c23.5-11.6 46.2-24.8 67.9-39.3L400.5 400.8c0 0 0.1 0 0.1-0.1l395 450.4c19.7-17.3 38.2-35.8 55.5-55.5l-450.4-395c0 0 0-0.1 0.1-0.1L899 733.4c14.5-21.7 27.6-44.4 39.3-68l-537.4-265c0 0 0-0.1 0-0.1l567.4 192.6c8.3-24.6 15.1-49.9 20.2-75.8L401 400.2c0 0 0-0.1 0-0.1L998.7 439.2z';

export const RadialSunburst: React.FC<{ themeRevision?: Partial<BackgroundThemeRevision> }> = (props) => {
  const { colorId1, colorId2 } = props.themeRevision || {};
  const { isDarkMode } = useTheme();
  const bodyStyleVersion = useBodyStyleVersion();

  const computedColors = useMemo(() => {
    const cssVar1 = getCssVariableForColorId(colorId1 || ColorId.Primary2, isDarkMode);
    const cssVar2 = getCssVariableForColorId(colorId2 || ColorId.Primary4, isDarkMode);
    const rootStyles = getComputedStyle(document.body);
    const c1 = rgbStringToHex(rootStyles.getPropertyValue(cssVar1).trim() || '255 255 255');
    const c2 = rgbStringToHex(rootStyles.getPropertyValue(cssVar2).trim() || '0 238 255');
    return { c1, c2 };
  }, [colorId1, colorId2, isDarkMode, bodyStyleVersion]);

  const svgContent = useMemo(() => {
    const { c1, c2 } = computedColors;
    return `
      <svg xmlns="http://www.w3.org/2000/svg" width="100%" height="100%" viewBox="0 0 800 800" preserveAspectRatio="xMidYMid slice">
        <defs>
          <radialGradient id="bg" cx="400" cy="400" r="50%" gradientUnits="userSpaceOnUse">
            <stop offset="0" stop-color="#ffffff"/>
            <stop offset="1" stop-color="${c2}"/>
          </radialGradient>
          <radialGradient id="centerFade" cx="400" cy="400" r="180" gradientUnits="userSpaceOnUse">
            <stop offset="0" stop-color="${c1}" stop-opacity="1"/>
            <stop offset="1" stop-color="${c2}" stop-opacity="0"/>
          </radialGradient>
        </defs>
        <rect fill="url(#bg)" width="800" height="800"/>
        <g fill-opacity=".8">
          <path fill="${c1}" d="${SUNBURST_PATH}"/>
        </g>
        <circle cx="400" cy="400" r="180" fill="url(#centerFade)"/>
      </svg>
    `;
  }, [computedColors]);

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        width: '100%',
        height: '100%',
        backgroundColor: computedColors.c1,
        backgroundImage: `url("data:image/svg+xml,${encodeURIComponent(svgContent)}")`,
        backgroundSize: 'cover',
        backgroundPosition: 'center center',
        backgroundRepeat: 'no-repeat',
        pointerEvents: 'none',
      }}
    />
  );
};
